import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import IntroWizard from '../components/IntroWizard';
import PageContainer from '../components/PageContainer';
import { getDocuments, Document } from '../api/documents';
import { getFeedbackSummary, FeedbackSummary } from '../api/feedback';
import { isSuperuser } from '../api/auth';

const WIZARD_KEY = 'nnu_intro_done';

interface HealthStat {
  pending: number;
  failed: number;
  indexed: number;
  loading: boolean;
  error: string | null;
}

interface FeedbackStat {
  summary: FeedbackSummary | null;
  loading: boolean;
  error: string | null;
}

const Dashboard: React.FC = () => {
  const [showWizard, setShowWizard] = useState(() => !localStorage.getItem(WIZARD_KEY));
  const [health, setHealth] = useState<HealthStat>({ pending: 0, failed: 0, indexed: 0, loading: true, error: null });
  const [feedback, setFeedback] = useState<FeedbackStat>({ summary: null, loading: true, error: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const docs: Document[] = await getDocuments();
        if (cancelled) return;
        // Status values used by the backend (db/models.py): "draft" (uploaded
        // but not ingested), "active" (indexed and ready), "ingestion_failed"
        // (something broke mid-ingest), "inactive" (intentionally disabled).
        const pending = docs.filter(d => d.status === 'draft').length;
        const failed = docs.filter(d => d.status === 'ingestion_failed').length;
        const indexed = docs.filter(d => d.status === 'active').length;
        setHealth({ pending, failed, indexed, loading: false, error: null });
      } catch (e: any) {
        if (!cancelled) setHealth(h => ({ ...h, loading: false, error: e?.message || 'Failed to load' }));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const summary = await getFeedbackSummary(14, 5);
        if (!cancelled) setFeedback({ summary, loading: false, error: null });
      } catch (e: any) {
        if (!cancelled) setFeedback({ summary: null, loading: false, error: e?.message || 'Failed to load' });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleWizardComplete = () => {
    localStorage.setItem(WIZARD_KEY, '1');
    setShowWizard(false);
  };

  const needsAttention = health.failed > 0 || health.pending > 0;

  return (
    <PageContainer title="Dashboard" subtitle="Content, data, and widget settings for the chatbot.">
      {showWizard && <IntroWizard onComplete={handleWizardComplete} />}

      {/* Hero status card — glance, act, leave */}
      <section>
        <div className={`border rounded-xl bg-white ${needsAttention ? 'border-primary-300' : 'border-primary-200'}`}>
          <div className="px-6 py-6 lg:px-8 lg:py-7">
            {health.loading ? (
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-primary-300 animate-pulse" />
                <span className="text-sm text-primary-500">Checking content…</span>
              </div>
            ) : health.error ? (
              <div>
                <div className="text-base font-medium text-primary-900">Status unavailable</div>
                <div className="mt-1 text-sm text-primary-500">{health.error}</div>
              </div>
            ) : needsAttention ? (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary-900" />
                    <span className="eyebrow">Needs attention</span>
                  </div>
                  <h2 className="display-md text-2xl text-primary-900">
                    {[
                      health.failed > 0 ? `${health.failed} document${health.failed === 1 ? '' : 's'} failed to publish` : null,
                      health.pending > 0 ? `${health.pending} document${health.pending === 1 ? '' : 's'} waiting to publish` : null,
                    ].filter(Boolean).join(' · ')}
                  </h2>
                  <p className="mt-2 text-sm text-primary-500">Review these so the chatbot can start using them.</p>
                </div>
                <Link
                  to={health.failed > 0 ? '/documents?status=ingestion_failed' : '/documents?status=draft'}
                  className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-primary-900 hover:bg-primary-800 rounded-lg border border-primary-900 transition-colors whitespace-nowrap self-start sm:self-auto"
                >
                  Review documents
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                  </svg>
                </Link>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary-900" />
                    <span className="eyebrow">All good</span>
                  </div>
                  <h2 className="display-md text-2xl text-primary-900">
                    {health.indexed} document{health.indexed === 1 ? '' : 's'} published and ready
                  </h2>
                  <p className="mt-2 text-sm text-primary-500">Nothing is stuck. Add a new document or preview what the chatbot finds.</p>
                </div>
                <Link
                  to="/documents/create"
                  className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-primary-900 bg-white hover:bg-primary-50 rounded-lg border border-primary-300 transition-colors whitespace-nowrap self-start sm:self-auto"
                >
                  Add document
                </Link>
              </div>
            )}
          </div>

          {/* Meta strip: three counts, borders over shadows */}
          {!health.loading && !health.error && (
            <div className="grid grid-cols-3 border-t border-primary-200">
              <StatCell label="Published" value={health.indexed} />
              <StatCell label="Pending" value={health.pending} dim={health.pending === 0} />
              <StatCell label="Failed" value={health.failed} dim={health.failed === 0} />
            </div>
          )}
        </div>
      </section>

      {/* User feedback — thumbs up/down signal from the widget */}
      <FeedbackSection feedback={feedback} />

      {/* Primary actions — typography hierarchy, grayscale, borders over shadows */}
      <section>
        <h3 className="eyebrow mb-4">Knowledge</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <ActionRow
            to="/documents"
            title="Documents"
            subtitle="Upload, publish, and review the chatbot's content."
          />
          <ActionRow
            to="/retrieval-test"
            title="Preview answers"
            subtitle="Try a question and see exactly what the chatbot would find."
          />
          <ActionRow
            to="/data/domains"
            title="Domains"
            subtitle="Separate chatbots per audience — branding, tone, and content scope."
          />
          {isSuperuser() && (
            <ActionRow
              to="/settings"
              title="Settings"
              subtitle="Assistant tone, fallback messages, and widget appearance."
            />
          )}
        </div>
      </section>

      <section>
        <div className="flex items-center justify-between mb-4">
          <h3 className="eyebrow">University data</h3>
          <Link to="/data" className="text-xs font-medium text-primary-700 hover:text-primary-900 underline underline-offset-4 decoration-primary-300 hover:decoration-primary-500 transition-colors">
            View all
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { title: 'Programs', path: '/data/programs' },
            { title: 'Contacts', path: '/data/contacts' },
            { title: 'Departments', path: '/data/departments' },
            { title: 'Admission Grades', path: '/data/admission-grades' },
          ].map(item => (
            <Link
              key={item.path}
              to={item.path}
              className="border border-primary-200 rounded-lg px-4 py-4 text-center bg-white hover:border-primary-400 transition-colors"
            >
              <span className="text-sm font-medium text-primary-800">{item.title}</span>
            </Link>
          ))}
        </div>
      </section>
    </PageContainer>
  );
};

const StatCell: React.FC<{ label: string; value: number; dim?: boolean; suffix?: string }> = ({ label, value, dim, suffix }) => (
  <div className="px-6 py-5 border-primary-200 [&:not(:last-child)]:border-e">
    <div className={`display-md text-3xl tabular-nums ${dim ? 'text-primary-400' : 'text-primary-900'}`}>{value}{suffix ?? ''}</div>
    <div className="mt-2 eyebrow">{label}</div>
  </div>
);

const FeedbackSection: React.FC<{ feedback: FeedbackStat }> = ({ feedback }) => {
  const { summary, loading, error } = feedback;

  const upCount = summary?.totals.find(t => t.rating === 1)?.count ?? 0;
  const downCount = summary?.totals.find(t => t.rating === -1)?.count ?? 0;
  const total = upCount + downCount;
  const upPct = total > 0 ? Math.round((upCount / total) * 100) : 0;

  const recentComments = (summary?.recent ?? []).filter(r => (r.comment ?? '').trim().length > 0);

  return (
    <section>
      <div className="flex items-end justify-between mb-4">
        <h3 className="eyebrow">User feedback · last {summary?.window_days ?? 14} days</h3>
        {summary && total > 0 && (
          <span className="text-xs text-primary-500 tabular-nums">{total} rating{total === 1 ? '' : 's'}</span>
        )}
      </div>

      <div className="border border-primary-200 rounded-xl bg-white">
        {loading ? (
          <div className="px-6 py-6 flex items-center gap-3">
            <div className="w-2 h-2 rounded-full bg-primary-300 animate-pulse" />
            <span className="text-sm text-primary-500">Loading feedback…</span>
          </div>
        ) : error ? (
          <div className="px-6 py-6">
            <div className="text-base font-medium text-primary-900">Feedback unavailable</div>
            <div className="mt-1 text-sm text-primary-500">{error}</div>
          </div>
        ) : total === 0 ? (
          <div className="px-6 py-6">
            <div className="text-base font-medium text-primary-900">No ratings yet</div>
            <div className="mt-1 text-sm text-primary-500">Once users click thumbs up or down in the widget, ratings appear here.</div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-3">
              <StatCell label="Helpful" value={upCount} />
              <StatCell label="Not helpful" value={downCount} dim={downCount === 0} />
              <StatCell label="Positive" value={upPct} suffix="%" />
            </div>
            {recentComments.length > 0 && (
              <div className="border-t border-primary-200 px-6 py-5">
                <div className="eyebrow mb-3">Recent comments</div>
                <ul className="space-y-3">
                  {recentComments.slice(0, 3).map(item => (
                    <li key={item.id} className="text-sm leading-relaxed">
                      <div className="flex items-start gap-2">
                        <span className={`inline-block mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0 ${item.rating === 1 ? 'bg-emerald-500' : 'bg-red-500'}`} />
                        <div className="min-w-0">
                          <div className="text-primary-800">{item.comment}</div>
                          {item.query && (
                            <div className="mt-0.5 text-primary-500 truncate">Q: {item.query}</div>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

const ActionRow: React.FC<{ to: string; title: string; subtitle: string }> = ({ to, title, subtitle }) => (
  <Link
    to={to}
    className="group border border-primary-200 rounded-lg px-5 py-5 bg-white hover:border-primary-400 transition-colors flex items-start justify-between gap-4"
  >
    <div className="min-w-0">
      <h4 className="text-base font-semibold text-primary-900 tracking-tight leading-tight">{title}</h4>
      <p className="mt-1.5 text-sm text-primary-500 leading-relaxed">{subtitle}</p>
    </div>
    <svg
      className="w-4 h-4 mt-1 flex-shrink-0 text-primary-400 group-hover:text-primary-700 transition-colors"
      fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
    </svg>
  </Link>
);

export default Dashboard;
